// src/components/auth/GoogleSignInButton.tsx
// Google Sign-In button using Google Identity Services

import React, { useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { Button, CircularProgress } from '@mui/material';
import { OAuthButtonProps, OAuthError } from '../../types/oauth';
import { OAUTH_CONFIG } from '../../config/oauth';
import oauthService from '../../services/oauthService';
import clarityService from '../../services/clarityService';

// Google icon SVG for custom button styling
const GoogleIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

/**
 * Google Sign-In Button Component
 * Custom styled button that matches your UI
 */
const GoogleSignInButton: React.FC<OAuthButtonProps> = ({
  onSuccess,
  onError,
  disabled = false,
  variant = 'outlined',
  fullWidth = true,
  text,
  context = 'signin',
}) => {
  const [isLoading, setIsLoading] = React.useState(false);

  // Default button text based on context
  const buttonText = text || (context === 'signup' ? 'Sign up with Google' : 'Sign in with Google');

  /**
   * Handle Google Sign-In error
   */
  const handleError = useCallback(() => {
    clarityService.event(OAUTH_CONFIG.ANALYTICS_EVENTS.LOGIN_FAILED);

    const error: OAuthError = {
      code: 'GOOGLE_ERROR',
      message: OAUTH_CONFIG.ERRORS.AUTHENTICATION_FAILED,
    };
    onError(error);
    setIsLoading(false);
  }, [onError]);

  // Google login hook - returns access_token which backend can verify
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      clarityService.event(OAUTH_CONFIG.ANALYTICS_EVENTS.LOGIN_STARTED);

      try {
        const { user, isNewUser } = await oauthService.authenticateWithGoogle({
          credential: tokenResponse.access_token,
          clientId: '',
          select_by: 'btn',
        });

        if (isNewUser) {
          clarityService.event(OAUTH_CONFIG.ANALYTICS_EVENTS.SIGNUP_SUCCESS);
          clarityService.upgrade('New User OAuth Registration');
        } else {
          clarityService.event(OAUTH_CONFIG.ANALYTICS_EVENTS.LOGIN_SUCCESS);
        }

        onSuccess(user, isNewUser);
      } catch (error: unknown) {
        clarityService.event(OAUTH_CONFIG.ANALYTICS_EVENTS.LOGIN_FAILED);

        const oauthError: OAuthError =
          error && typeof error === 'object' && 'code' in error
            ? (error as OAuthError)
            : {
                code: 'AUTH_FAILED',
                message: OAUTH_CONFIG.ERRORS.AUTHENTICATION_FAILED,
                originalError: error,
              };

        onError(oauthError);
      } finally {
        setIsLoading(false);
      }
    },
    onError: handleError,
  });

  return (
    <Button
      variant={variant}
      fullWidth={fullWidth}
      disabled={disabled || isLoading}
      onClick={() => {
        setIsLoading(true);
        googleLogin();
      }}
      startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <GoogleIcon />}
      sx={{
        py: 1.5,
        textTransform: 'none',
        fontSize: '0.9375rem',
        fontWeight: 500,
        borderColor: 'divider',
        color: 'text.primary',
        backgroundColor: 'background.paper',
        '&:hover': {
          backgroundColor: 'action.hover',
          borderColor: 'text.secondary',
        },
      }}
    >
      {isLoading ? 'Signing in...' : buttonText}
    </Button>
  );
};

export default GoogleSignInButton;
